import { useEffect, useState, useCallback, useRef } from 'react';
import { Table, Button, Space, Tag, Popconfirm, message, Select, Typography, Spin } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ToolOutlined, AppstoreAddOutlined, CopyOutlined } from '@ant-design/icons';
import { api } from '../api';
import { useUrlParam } from '../hooks/useUrlParam';
import ProductForm from './ProductForm';

const { Text } = Typography;

const TYPE_COLORS = { component: 'orange', addon: 'blue', variant: 'purple' };

export default function ProductsTab({ storeId, meta }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useUrlParam('type');
  const [categoryFilter, setCategoryFilter] = useUrlParam('category');
  const [categories, setCategories] = useState([]);
  const prevStoreId = useRef(storeId);
  const [editing, setEditing] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    if (!storeId) return;
    if (prevStoreId.current !== storeId) {
      setCategoryFilter(null);
      prevStoreId.current = storeId;
    }
    api.categories.list(storeId).then(setCategories).catch(() => {});
  }, [storeId, setCategoryFilter]);

  const load = useCallback(async () => {
    if (!storeId) return;
    setLoading(true);
    try {
      const rows = await api.products.list(storeId, typeFilter, categoryFilter);
      setProducts(rows);
      setExpandedRows({});
      setExpandedRowKeys([]);
    } catch (err) {
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [storeId, typeFilter, categoryFilter]);

  useEffect(() => { load(); }, [load]);

  async function handleDelete(id, parentId) {
    try {
      await api.products.delete(id);
      message.success('Gelöscht');
      if (parentId) {
        setExpandedRows(prev => ({ ...prev, [parentId]: undefined }));
        loadSubproducts(parentId);
      } else {
        setProducts(prev => prev.filter(p => p.id !== id));
      }
    } catch (err) {
      message.error(err.message);
    }
  }

  async function openEdit(id) {
    try {
      const product = await api.products.get(id);
      setEditing(product);
    } catch (err) {
      message.error(err.message);
    }
  }

  async function loadSubproducts(id) {
    setExpandedRows(prev => ({ ...prev, [id]: { loading: true } }));
    try {
      const full = await api.products.get(id);
      setExpandedRows(prev => ({
        ...prev,
        [id]: {
          loading:    false,
          variants:   full.variants   || [],
          components: full.components || [],
          addons:     full.addons     || [],
        },
      }));
    } catch {
      setExpandedRows(prev => ({ ...prev, [id]: { loading: false, variants: [], components: [], addons: [] } }));
    }
  }

  function handleExpand(expanded, record) {
    setExpandedRowKeys(prev =>
      expanded ? [...prev, record.id] : prev.filter(k => k !== record.id)
    );
    if (expanded && !expandedRows[record.id]) loadSubproducts(record.id);
  }

  function handleSaved() {
    setEditing(null);
    load();
  }

  function subColumns(parentId, deleteLabel) {
    return [
      {
        title: 'Name', dataIndex: 'name',
        render: (name, row) => {
          const icon = row.product_type_name === 'component'
            ? <ToolOutlined style={{ color: row.inherited ? '#ccc' : '#a07838' }} />
            : row.product_type_name === 'variant'
              ? <CopyOutlined style={{ color: '#722ed1' }} />
              : <AppstoreAddOutlined style={{ color: row.inherited ? '#ccc' : '#1677ff' }} />;
          return (
            <Space>
              {icon}
              <span style={{ color: row.inherited ? '#aaa' : undefined }}>{name || '—'}</span>
              {row.inherited && (
                <Text type="secondary" style={{ fontSize: 11 }}>({row.inherited_from})</Text>
              )}
            </Space>
          );
        },
      },
      {
        title: 'Preis (netto)', dataIndex: 'net_price', width: 130, align: 'right',
        render: (v, row) => {
          const val = v != null
            ? <span style={{ fontVariantNumeric: 'tabular-nums' }}>{Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
            : <Text type="secondary">—</Text>;
          return row.inherited ? <span style={{ color: '#aaa' }}>{val}</span> : val;
        },
      },
      {
        title: 'Lager', dataIndex: 'stock', width: 70, align: 'right',
        render: (v, row) => <span style={{ color: row.inherited ? '#aaa' : undefined }}>{v ?? '—'}</span>,
      },
      {
        title: 'Kaufbar', dataIndex: 'is_purchasable', width: 80,
        render: (v, row) => row.inherited
          ? <Tag color="default" style={{ opacity: 0.5 }}>{v ? 'Ja' : 'Nein'}</Tag>
          : <Tag color={v ? 'green' : 'default'}>{v ? 'Ja' : 'Nein'}</Tag>,
      },
      {
        title: '', width: 90,
        render: (_, row) => row.inherited ? null : (
          <Space>
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row.id)} />
            <Popconfirm
              title={`${deleteLabel} löschen?`}
              okText="Löschen" okButtonProps={{ danger: true }} cancelText="Abbrechen"
              onConfirm={() => handleDelete(row.id, parentId)}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ),
      },
    ];
  }

  const columns = [
    {
      title: 'Name', dataIndex: 'name',
      render: name => name || <Text type="secondary">—</Text>,
    },
    {
      title: 'Typ', dataIndex: 'product_type_name', width: 120,
      render: name => <Tag color={TYPE_COLORS[name]}>{name}</Tag>,
      filters: meta.productTypes.map(pt => ({ text: pt.name, value: pt.name })),
      onFilter: (val, row) => row.product_type_name === val,
    },
    {
      title: 'Preis (netto)', dataIndex: 'net_price', width: 130, align: 'right',
      render: v => v != null
        ? <span style={{ fontVariantNumeric: 'tabular-nums' }}>{Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Lager', dataIndex: 'stock', width: 80, align: 'right',
      render: v => v ?? <Text type="secondary">—</Text>,
    },
    {
      title: 'Kaufbar', dataIndex: 'is_purchasable', width: 90,
      render: v => <Tag color={v ? 'green' : 'default'}>{v ? 'Ja' : 'Nein'}</Tag>,
    },
    {
      title: '', width: 90,
      render: (_, row) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row.id)} />
          <Popconfirm
            title="Produkt löschen?"
            okText="Löschen" okButtonProps={{ danger: true }} cancelText="Abbrechen"
            onConfirm={() => handleDelete(row.id, null)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Select
          allowClear
          placeholder="Alle Typen"
          style={{ width: 180 }}
          value={typeFilter}
          onChange={setTypeFilter}
          options={meta.productTypes.map(pt => ({ value: pt.name, label: pt.name }))}
        />
        <Select
          allowClear
          placeholder="Alle Kategorien"
          style={{ width: 200 }}
          value={categoryFilter}
          onChange={setCategoryFilter}
          showSearch
          filterOption={(input, opt) => (opt?.label || '').toLowerCase().includes(input.toLowerCase())}
          options={categories.map(c => ({ value: c.id, label: c.name }))}
        />
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing({})}>
          Neues Produkt
        </Button>
      </Space>
      <Table
        dataSource={products}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="middle"
        pagination={{ pageSize, showSizeChanger: true, onShowSizeChange: (_, size) => setPageSize(size) }}
        expandable={{
          expandedRowKeys,
          onExpand: handleExpand,
          rowExpandable: record =>
            record.component_count > 0 || record.addon_count > 0 || record.variant_count > 0,
          expandedRowRender: record => {
            const state = expandedRows[record.id];
            if (!state || state.loading) return <Spin size="small" style={{ margin: 12 }} />;
            const hasVariants   = state.variants?.length > 0;
            const hasComponents = state.components?.length > 0;
            const hasAddons     = state.addons?.length > 0;
            if (!hasVariants && !hasComponents && !hasAddons) {
              return <Text type="secondary" style={{ padding: '8px 16px', display: 'block' }}>Keine Unterprodukte zugeordnet</Text>;
            }
            return (
              <div style={{ paddingLeft: 32, paddingBottom: 8 }}>
                {hasVariants && (
                  <>
                    <Text strong style={{ display: 'block', marginBottom: 6, color: '#722ed1' }}>Varianten</Text>
                    <Table
                      dataSource={state.variants}
                      columns={subColumns(record.id, 'Variante')}
                      rowKey="id" size="small" pagination={false}
                      style={{ marginBottom: hasComponents || hasAddons ? 16 : 0 }}
                    />
                  </>
                )}
                {hasComponents && (
                  <>
                    <Text strong style={{ display: 'block', marginBottom: 6, color: '#a07838' }}>Komponenten</Text>
                    <Table
                      dataSource={state.components}
                      columns={subColumns(record.id, 'Komponente')}
                      rowKey="id" size="small" pagination={false}
                      style={{ marginBottom: hasAddons ? 16 : 0 }}
                    />
                  </>
                )}
                {hasAddons && (
                  <>
                    <Text strong style={{ display: 'block', marginBottom: 6, color: '#1677ff' }}>Addons</Text>
                    <Table
                      dataSource={state.addons}
                      columns={subColumns(record.id, 'Addon')}
                      rowKey="id" size="small" pagination={false}
                    />
                  </>
                )}
              </div>
            );
          },
        }}
      />
      {editing !== null && (
        <ProductForm
          product={editing.id ? editing : null}
          storeId={storeId}
          meta={meta}
          onSave={handleSaved}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
